import React, { useState, useRef } from 'react';
import { text } from '../text';
import { hooks } from '../hooks';
import { components } from '../components';
import { theme } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';
import { MdCheckCircle, MdError, MdWarning, MdAttachFile, MdClose } from 'react-icons/md';

const AttachFileIcon = MdAttachFile as any;
const CloseIcon = MdClose as any;

const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;

export const LoanRequest: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { t } = useLanguage();
  const tl = (t as any).loanRequest || {};

  const [amount, setAmount] = useState(''); // display value: "5.000.000"
  const [tenor, setTenor] = useState('');

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    setAmount(digits ? parseInt(digits, 10).toLocaleString('id-ID') : '');
  };

  const rawAmount = amount.replace(/\./g, '');
  const [tenorUnit, setTenorUnit] = useState<'months' | 'weeks'>('months');
  const [reason, setReason] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    detail?: string;
    type: 'success' | 'error' | 'warning';
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void, detail?: string) => {
    setAlertConfig({ isOpen: true, title, message, detail, type, onClose });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
    if (onClose) onClose();
  };

  const handleSubmit = async () => {
    if (!rawAmount || !tenor) {
      showAlert(t.common.warning, tl.validation || 'Mohon lengkapi jumlah pinjaman dan tenor.', 'warning');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('amount', String(parseFloat(rawAmount)));
      formData.append('tenor', String(parseInt(tenor)));
      formData.append('tenor_unit', tenorUnit);
      formData.append('reason', reason.trim());
      if (attachmentFile) formData.append('attachment', attachmentFile);

      await axiosInstance.post('/loans', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showAlert(t.common.success, tl.success || 'Pengajuan kasbon berhasil dikirim!', 'success', () => navigate(-1));
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showAlert(t.common.error, err?.response?.data?.message || tl.error || 'Gagal mengirim pengajuan.', 'error', undefined, detail);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 10,
    border: '1px solid #E5E7EB',
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    color: theme.colors.mainDark,
    backgroundColor: '#FAFAFA',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    color: '#6B7280',
    fontWeight: '600',
    marginBottom: 6,
    display: 'block',
  };

  const getAlertIcon = () => {
    if (alertConfig.type === 'success') return <CheckCircleIcon size={28} color="#10B981" />;
    if (alertConfig.type === 'error') return <ErrorIcon size={28} color="#EF4444" />;
    return <WarningIcon size={28} color="#F59E0B" />;
  };

  return (
    <div id='screen'>
      <components.Header title={tl.title || 'Pengajuan Kasbon'} goBack={true} />

      <main className='container' style={{ marginTop: 60, paddingBottom: 110, height: 'calc(100vh - 60px)', overflowY: 'auto' }}>

        <div style={{ backgroundColor: theme.colors.white, borderRadius: 16, padding: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #F3F4F6', marginBottom: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{tl.amount || 'Jumlah Pinjaman (Rp)'}</label>
            <input
              style={inputStyle}
              type="text"
              inputMode="numeric"
              placeholder={tl.amountPlaceholder || 'Contoh: 5.000.000'}
              value={amount}
              onChange={handleAmountChange}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{tl.tenor || 'Tenor'}</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                type="number"
                min="1"
                placeholder={tl.tenorPlaceholder || 'Contoh: 3'}
                value={tenor}
                onChange={(e) => setTenor(e.target.value)}
              />
              <select
                style={{ ...inputStyle, flex: 0, width: 120, cursor: 'pointer' }}
                value={tenorUnit}
                onChange={(e) => setTenorUnit(e.target.value as 'months' | 'weeks')}
              >
                <option value="months">{tl.months || 'Bulan'}</option>
                <option value="weeks">{tl.weeks || 'Minggu'}</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>{tl.reason || 'Alasan Pengajuan'} <span style={{ fontWeight: '400', color: '#9CA3AF' }}>({t.common.optional || 'opsional'})</span></label>
            <textarea
              style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
              placeholder={tl.reasonPlaceholder || 'Jelaskan keperluan pengajuan kasbon...'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div>
            <label style={labelStyle}>{tl.attachment || 'Dokumen Pendukung'} <span style={{ fontWeight: '400', color: '#9CA3AF' }}>({t.common.optional || 'opsional'})</span></label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              style={{ display: 'none' }}
              onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
            />
            {attachmentFile ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 16px', borderRadius: 10,
                border: `1px solid ${theme.colors.mainColor}`,
                backgroundColor: '#F0FDF4',
              }}>
                <AttachFileIcon size={18} color={theme.colors.mainColor} />
                <text.T14 style={{ flex: 1, color: theme.colors.mainDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {attachmentFile.name}
                </text.T14>
                <div onClick={() => { setAttachmentFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} style={{ cursor: 'pointer', flexShrink: 0 }}>
                  <CloseIcon size={18} color="#6B7280" />
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '14px 16px', borderRadius: 10,
                  border: '1.5px dashed #D1D5DB', cursor: 'pointer',
                  backgroundColor: '#FAFAFA',
                }}
              >
                <AttachFileIcon size={18} color="#9CA3AF" />
                <text.T14 style={{ color: '#9CA3AF' }}>{tl.attachmentPlaceholder || 'Pilih file (PDF, gambar, dokumen)'}</text.T14>
              </div>
            )}
          </div>
        </div>

        <components.Button
          title={isLoading ? (tl.submitting || 'Mengajukan...') : (tl.submit || 'Ajukan Kasbon')}
          onClick={isLoading ? undefined : handleSubmit}
        />
      </main>

      {alertConfig.isOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 320, textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ marginBottom: 12 }}>{getAlertIcon()}</div>
            <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
            <text.T14 style={{ color: '#6B7280', marginBottom: alertConfig.detail ? 8 : 24 }}>{alertConfig.message}</text.T14>
            {alertConfig.detail && (
              <div style={{ backgroundColor: '#F3F4F6', borderRadius: 8, padding: '8px 12px', marginBottom: 20, textAlign: 'left' }}>
                <text.T12 style={{ color: '#6B7280', fontFamily: 'monospace', wordBreak: 'break-all' }}>{alertConfig.detail}</text.T12>
              </div>
            )}
            <components.Button title="OK" onClick={hideAlert} />
          </div>
        </div>
      )}
    </div>
  );
};
